import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import SEO from "@/lib/seo";
import { useCustomer, startGoogleLogin } from "@/lib/customer";

export const INPUT = "mt-1.5 w-full rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-mint/40 transition";

export function GoogleButton({ testid = "customer-google-btn" }) {
  return (
    <button type="button" data-testid={testid} onClick={startGoogleLogin} className="w-full rounded-full border border-line bg-white py-3 text-sm font-medium text-ink hover:bg-cream-light transition-colors flex items-center justify-center gap-3">
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C41.4 35.4 44 30.1 44 24c0-1.3-.1-2.3-.4-3.5z"/></svg>
      Continuer avec Google
    </button>
  );
}

export default function Connexion() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "inscription" ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState("");
  const { setCustomer } = useCustomer();
  const navigate = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (mode === "forgot") {
        const { data } = await api.post("/customer/forgot-password", { email: form.email });
        setSent(data.message);
        return;
      }
      const url = mode === "register" ? "/customer/register" : "/customer/login";
      const body = mode === "register" ? form : { email: form.email, password: form.password };
      const { data } = await api.post(url, body);
      setCustomer(data);
      if (data.linked_orders) toast.success(`${data.linked_orders} commande(s) passée(s) rattachée(s) à votre compte`);
      else toast.success(mode === "register" ? "Bienvenue ! Votre compte est créé." : "Ravie de vous revoir !");
      navigate("/compte", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (m) => { setMode(m); setError(""); setSent(""); };
  const title = mode === "register" ? "Créer mon compte" : mode === "forgot" ? "Mot de passe oublié" : "Me connecter";

  return (
    <div data-testid="customer-auth-page" className="min-h-[85vh] bg-cream-light pt-32 md:pt-40 pb-20 px-6">
      <SEO title={title} description="Accédez à votre espace cliente Tendres Tétées : vos commandes, vos informations de livraison." />
      <div className="max-w-md mx-auto bg-white rounded-3xl border border-line p-8 md:p-10 shadow-[0_20px_60px_rgba(16,114,105,0.1)]">
        <p className="eyebrow mb-3">Espace cliente</p>
        <h1 className="font-serif text-3xl md:text-4xl text-teal-dark">{title}</h1>
        <p className="mt-3 text-sm text-ink/70">
          {mode === "register" ? "Retrouvez toutes vos commandes et commandez plus vite la prochaine fois." : mode === "forgot" ? "Indiquez votre email, nous vous envoyons un lien pour choisir un nouveau mot de passe." : "Retrouvez vos commandes et vos informations de livraison."}
        </p>

        {mode !== "forgot" && (
          <div className="mt-7">
            <GoogleButton />
            <div className="my-6 flex items-center gap-4 text-xs text-mauve"><span className="h-px flex-1 bg-line" />ou par email<span className="h-px flex-1 bg-line" /></div>
          </div>
        )}

        <form onSubmit={submit} data-testid="customer-auth-form" className={`space-y-4 ${mode === "forgot" ? "mt-7" : ""}`}>
          {mode === "register" && (
            <>
              <label className="block text-sm font-medium text-ink">Nom complet *
                <input required data-testid="customer-name-input" value={form.name} onChange={set("name")} placeholder="Ex : Aïssatou Diop" className={INPUT} />
              </label>
              <label className="block text-sm font-medium text-ink">Téléphone (pour retrouver vos commandes passées)
                <input type="tel" data-testid="customer-phone-input" value={form.phone} onChange={set("phone")} placeholder="Ex : +221 77 123 45 67" className={INPUT} />
              </label>
            </>
          )}
          <label className="block text-sm font-medium text-ink">Email *
            <input required type="email" data-testid="customer-email-input" value={form.email} onChange={set("email")} className={INPUT} />
          </label>
          {mode !== "forgot" && (
            <label className="block text-sm font-medium text-ink">Mot de passe *
              <input required type="password" autoComplete={mode === "register" ? "new-password" : "current-password"} data-testid="customer-password-input" value={form.password} onChange={set("password")} className={INPUT} />
              {mode === "register" && <span className="mt-1 block text-xs text-ink/60 font-normal">Au moins 8 caractères.</span>}
            </label>
          )}
          {error && <p data-testid="customer-auth-error" className="text-sm text-[#b04a4a] bg-[#b04a4a]/10 rounded-xl px-4 py-2.5">{error}</p>}
          {sent && <p data-testid="customer-forgot-sent" className="text-sm text-teal-dark bg-mint/25 rounded-xl px-4 py-2.5">{sent}</p>}
          <button type="submit" data-testid="customer-auth-submit" disabled={loading} className="w-full rounded-full bg-teal py-3.5 font-medium text-cream hover:bg-teal-dark transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {loading && <Loader2 size={16} className="animate-spin" />}
            {mode === "register" ? "Créer mon compte" : mode === "forgot" ? "Recevoir le lien" : "Me connecter"}
          </button>
        </form>

        <div className="mt-6 flex flex-col gap-2 text-sm text-center">
          {mode === "login" && (
            <>
              <button type="button" data-testid="customer-switch-forgot" onClick={() => switchMode("forgot")} className="text-mauve hover:text-teal transition-colors">Mot de passe oublié ?</button>
              <button type="button" data-testid="customer-switch-register" onClick={() => switchMode("register")} className="text-teal font-medium">Pas encore de compte ? Créer mon compte</button>
            </>
          )}
          {mode === "register" && <button type="button" data-testid="customer-switch-login" onClick={() => switchMode("login")} className="text-teal font-medium">Déjà un compte ? Me connecter</button>}
          {mode === "forgot" && <button type="button" data-testid="customer-switch-login" onClick={() => switchMode("login")} className="text-mauve hover:text-teal">← Retour à la connexion</button>}
        </div>
        <p className="mt-8 text-center text-xs text-mauve"><Link to="/boutique" className="link-underline">Continuer mes achats sans compte</Link></p>
      </div>
    </div>
  );
}
